import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter,Subscription } from 'rxjs';

import { TFolderItem } from '@core/interfaces/TFolderItem';
import { TSourceItem } from '@core/interfaces/TSourceItem';
import { HeaderComponent } from '@theme/header/header.component';

import { NewConfluenceModalComponent } from '../components/gingr-new-items-modals/new-confluence-modal/new-confluence-folder-modal.component';
import { NewFolderModalComponent } from '../components/gingr-new-items-modals/new-folder-modal/new-folder-modal.component';
import { NewVeevaModalComponent } from '../components/gingr-new-items-modals/new-veeva-modal/new-veeva-modal.component';
import { ConfirmationModalComponent } from '../components/modals/confirmation-modal/confirmation-modal.component';
import { EditSourceFolderModalComponent } from '../components/modals/edit-source-folder-modal/edit-source-folder-modal.component';
import { ImportSourceSelectorModalComponent } from '../components/modals/import-source-selector/import-source-selector-modal.component';
import { NewECRDataModalComponent } from '../components/modals/new-ecr-data-modal/new-ecr-data-modal.component';
import { SourceFolderModalComponent } from '../components/modals/source-folder-modal/source-folder-modal.component';
import { SourcesWorkspaceStateService } from './sources-workspace-state.service';
import { TContextGroup } from '@core/api/context/types';
import { TMaterialSourceFolder } from '@core/api/materials/types';
import {
  ContentShellComponent,
  ListItemComponent,
  PanelHeaderComponent,
  PanelSearchComponent,
  PanelTabsComponent,
} from '@shared/components/content-panel';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { CommonService } from '@shared/services/common.service';
import { ContextService } from '@shared/services/context.service';
import { FoldersService } from '@shared/services/folders.service';
import { isCustomIcon, TIconItem } from '@shared/utils/isCustomIcon';
import { isNotNullAndDefined } from '@shared/utils/isNotNullAndDefined';
import { isNullOrEmpty } from '@shared/utils/isNullOrEmpty';

import { ContextGroupsListComponent } from './context/context-groups-list/context-groups-list.component';
import { MaterialsFoldersComponent } from './materials/materials-folders.component';

@Component({
  selector: 'app-sources',
  templateUrl: './sources.component.html',
  styleUrl: './sources.component.scss',
  imports: [
    NgClass,
    RouterOutlet,
    MatIcon,
    MatButton,
    MatIconButton,
    MatMenuModule,
    MatTooltip,
    SpinnerComponent,
    MaterialsFoldersComponent,
    ContextGroupsListComponent,
    ContentShellComponent,
    HeaderComponent,
    PanelHeaderComponent,
    PanelSearchComponent,
    PanelTabsComponent,
    ListItemComponent,
  ],
  providers: [SourcesWorkspaceStateService],
})
export class SourcesComponent implements OnInit, OnDestroy {
  @ViewChild('contextGroupsList') contextGroupsList: ContextGroupsListComponent | undefined;

  private _subs: Subscription[] = [];
  protected selectedMaterialFolder: TMaterialSourceFolder | undefined;
  protected selectedContextGroup: TContextGroup | undefined;

  tabLabels: string[] = ['Sources', 'Materials', 'Context'];
  activeTabLabel = 'Sources';
  sourcesSearchQuery = '';

  constructor(
    public dialog: MatDialog,
    private _foldersService: FoldersService,
    private _commonService: CommonService,
    private _contextService: ContextService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly _workspace: SourcesWorkspaceStateService
  ) {}

  public ngOnInit(): void {
    this._subs.push(
      this._foldersService.getFoldersObservable().subscribe(res => {
        this._workspace.folders.set(res);
        this._syncWorkspaceFromRoute();
      })
    );
    this._subs.push(
      this._foldersService.getFoldersLoadingObservable().subscribe(res => {
        this._workspace.foldersLoading.set(res);
      })
    );
    this._subs.push(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        this._syncWorkspaceFromRoute();
      })
    );
    this._foldersService.fetchFolders();
    this._syncWorkspaceFromRoute();
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  protected addNewItem(): void {
    switch (this.activeTabLabel) {
      case 'Sources':
        this.addNewSourceModal();
        return;
      case 'Materials':
        this.addNewMaterialFolder();
        return;
      case 'Context':
        this.contextGroupsList?.openCreateGroupModal();
        return;
    }
  }

  protected getAddButtonTooltip(): string {
    switch (this.activeTabLabel) {
      case 'Sources':
        return 'Add new source';
      case 'Materials':
        return 'Add new material folder';
      case 'Context':
        return 'Add new context group';
      default:
        return 'Add new';
    }
  }

  protected onTabLabelChange(label: string): void {
    this.activeTabLabel = label;
    switch (label) {
      case 'Materials':
        void this.router.navigate(['/sources/materials']);
        return;
      case 'Context':
        void this.router.navigate(['/sources/context']);
        return;
      default:
        if (this._workspace.selectedSource()?._id) {
          const folderId =
            this._workspace.selectedSource()?.folder?.[0] ?? this._workspace.selectedFolder()?._id;
          if (folderId) {
            void this.router.navigate([
              '/sources/folder',
              folderId,
              'source',
              this._workspace.selectedSource()?._id,
            ]);
            return;
          }
        }

        if (this._workspace.selectedFolder()?._id) {
          void this.router.navigate(['/sources/folder', this._workspace.selectedFolder()?._id]);
          return;
        }

        void this.router.navigate(['/sources']);
    }
  }

  protected addNewMaterialFolder() {
    const sub = this.dialog
      .open(SourceFolderModalComponent)
      .componentInstance.folderChanged.subscribe(folder => {
        this.selectedMaterialFolder = folder;
        sub.unsubscribe();
      });
  }

  protected addNewSourceModal() {
    const dialogRef = this.dialog.open(ImportSourceSelectorModalComponent);
    const handleOption1Subscription = dialogRef.componentInstance.selectedSource.subscribe(type => {
      switch (type) {
        case 'confluence':
          this.dialog.open(NewConfluenceModalComponent, {
            width: '80vw',
            height: '100vh',
            maxWidth: '80vw',
            maxHeight: '100vh',
            position: { right: '0' },
            panelClass: 'new-confluence-modal',
            hasBackdrop: true,
            disableClose: false,
          });
          break;
        case 'manual':
          this.dialog.open(NewFolderModalComponent);
          break;
        case 'ecr':
          this.dialog.open(NewECRDataModalComponent, {
            width: '80vw',
            height: '100vh',
            maxWidth: '80vw',
            maxHeight: '100vh',
            position: { right: '0' },
            panelClass: 'new-ecr-data-modal',
            hasBackdrop: true,
            disableClose: false,
          });
          break;
        case 'veeva':
          this.dialog.open(NewVeevaModalComponent, {
            width: '80vw',
            height: '100vh',
            maxWidth: '80vw',
            maxHeight: '100vh',
            position: { right: '0' },
            panelClass: 'new-veeva-modal',
            hasBackdrop: true,
            disableClose: false,
          });
          break;
      }
      handleOption1Subscription.unsubscribe();
    });
  }

  public hasIcon(item: TFolderItem): boolean {
    return !isNullOrEmpty(item.icon);
  }

  protected isCustomIcon(item: TIconItem): boolean {
    return isCustomIcon(item);
  }

  protected getItemCss(item: TFolderItem): string {
    const classes: string[] = [];
    if (item.tinyIcon === true) {
      classes.push(`menu-icon__tiny`);
    }
    return classes.join(` `);
  }

  protected hasSuffix(item: TFolderItem): boolean {
    return isNotNullAndDefined(item.sufix);
  }

  public openDeleteItemModal(item: TFolderItem, _event?: Event): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: { text: `Are you sure you want to delete ${item.title}?` },
    });
    const dialogSubscription = dialogRef.componentInstance.actionCallback.subscribe(() => {
      this._foldersService.deleteFolder(item._id, () => {
        dialogRef.close();
        this._commonService.openSuccessSnackBar(`Folder ${item.title} was deleted`);
      });

      dialogSubscription.unsubscribe();
    });
  }

  public openEditSourceFolderModal(item: TFolderItem, _event?: Event): void {
    this.dialog.open(EditSourceFolderModalComponent, {
      data: { source: item },
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: 'edit-source-folder-modal',
      hasBackdrop: true,
      disableClose: false,
    });
  }

  protected goToFolder(folder: TFolderItem): void {
    this.router.navigate(['/sources/folder', folder._id]);
  }

  public handleCloseSource(parentFolderId: string): void {
    const foundFolder = this._workspace.folders().find(item => item._id === parentFolderId);
    if (foundFolder) {
      this.goToFolder(foundFolder);
    }
  }

  protected handleContextGroupSelected(group: TContextGroup): void {
    this.selectedContextGroup = group;
  }

  protected getTabTitle(): string {
    switch (this._workspace.selectedTab()) {
      case 'sources':
        return 'Sources';
      case 'materials':
        return 'Materials';
      case 'context':
        return 'Context';
      default:
        return 'Sources';
    }
  }

  protected hasBreadcrumbs(): boolean {
    return (
      this._workspace.selectedTab() === 'sources' &&
      (!!this._workspace.selectedFolder() || !!this._workspace.selectedSource())
    );
  }

  protected openSourcesRoot(): void {
    void this.router.navigate(['/sources']);
  }

  protected openSelectedFolder(): void {
    if (!this._workspace.selectedFolder()?._id) {
      return;
    }

    void this.router.navigate(['/sources/folder', this._workspace.selectedFolder()?._id]);
  }

  protected openSelectedSourceAnnotations(): void {
    const folderId =
      this._workspace.selectedSource()?.folder?.[0] ?? this._workspace.selectedFolder()?._id;
    const sourceId = this._workspace.selectedSource()?._id;
    if (!folderId || !sourceId) {
      return;
    }

    void this.router.navigate(['/sources/folder', folderId, 'source', sourceId, 'annotations']);
  }

  protected openSelectedSourceOverview(): void {
    const folderId =
      this._workspace.selectedSource()?.folder?.[0] ?? this._workspace.selectedFolder()?._id;
    const sourceId = this._workspace.selectedSource()?._id;
    if (!folderId || !sourceId) {
      return;
    }

    void this.router.navigate(['/sources/folder', folderId, 'source', sourceId]);
  }

  protected getContentTitle(): string {
    if (this._workspace.isAnnotationsView() && this._workspace.selectedSource()?.name) {
      return `${this._workspace.selectedSource()?.name} Annotations`;
    }

    if (this._workspace.selectedSource()?.name) {
      return this._workspace.selectedSource()?.name ?? '';
    }

    if (this._workspace.selectedFolder()?.title) {
      return this._workspace.selectedFolder()?.title ?? '';
    }

    return '';
  }

  protected getContentSubtitle(): string {
    if (this._workspace.isAnnotationsView()) {
      return 'Review Veeva annotations, generate PDF annotations, and add manual highlights without leaving Sources.';
    }

    if (this._workspace.selectedSource()) {
      return 'Review metadata, references, embeddings, and file actions for this source.';
    }

    if (this._workspace.selectedFolder()) {
      return 'Browse sources in this folder and open a source to view details.';
    }

    return 'Select a folder from the left to browse its sources.';
  }

  protected get selectedFolder(): TFolderItem | undefined {
    return this._workspace.selectedFolder() ?? undefined;
  }

  protected get selectedSource(): TSourceItem | undefined {
    return this._workspace.selectedSource() ?? undefined;
  }

  protected get foldersLoading(): boolean {
    return this._workspace.foldersLoading();
  }

  protected get folders(): TFolderItem[] {
    return this._workspace.folders();
  }

  protected get filteredFolders(): TFolderItem[] {
    const sorted = this._workspace.folders().slice().sort((a, b) => a.title.localeCompare(b.title));
    if (!this.sourcesSearchQuery.trim()) return sorted;
    const q = this.sourcesSearchQuery.toLowerCase();
    return sorted.filter(f => f.title.toLowerCase().includes(q));
  }

  protected get isAnnotationsView(): boolean {
    return this._workspace.isAnnotationsView();
  }



  private _syncWorkspaceFromRoute(): void {
    const url = this.router.url;

    if (url.includes('/sources/materials')) {
      this._workspace.selectedTab.set('materials');
      this.activeTabLabel = 'Materials';
    } else if (url.includes('/sources/context')) {
      this._workspace.selectedTab.set('context');
      this.activeTabLabel = 'Context';
    } else {
      this._workspace.selectedTab.set('sources');
      this.activeTabLabel = 'Sources';
    }

    this._workspace.isAnnotationsView.set(url.includes('/annotations'));

    const deepest = this._getDeepestSnapshot(this.route.snapshot);
    const folderId = deepest.paramMap.get('folderId') ?? deepest.paramMap.get('folder_id');
    const sourceId = deepest.paramMap.get('sourceId') ?? deepest.paramMap.get('source_id');

    const foundFolder = folderId
      ? (this._workspace.folders().find(item => item._id === folderId) ?? null)
      : null;
    this._workspace.selectedFolder.set(foundFolder);

    if (!sourceId) {
      this._workspace.selectedSource.set(null);
      return;
    }

    this._foldersService.getSource(sourceId, res => {
      this._workspace.selectedSource.set(res);
      const sourceFolderId = res.folder?.[0];
      if (!this._workspace.selectedFolder() && sourceFolderId) {
        const folder = this._workspace.folders().find(item => item._id === sourceFolderId) ?? null;
        this._workspace.selectedFolder.set(folder);
      }
    });
  }

  private _getDeepestSnapshot(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let current = snapshot;
    while (current.firstChild) {
      current = current.firstChild;
    }
    return current;
  }
}
